/* Real Shopify variants and multipart files; AI requests only go to the configured private-key backend. */
(() => {
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
  const MAX_AI_BYTES = 8 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const instances = new WeakMap();

  // The image provider supplies no percentage or ETA. These are deliberately
  // labelled estimates; only a successfully loaded result can reach 100%.
  function generationEstimate(elapsedSeconds, phase) {
    const elapsed = Math.max(0, elapsedSeconds);
    if (phase === 'ready') return { percent: 100, eta: 'Your preview is ready' };
    if (phase === 'finish') return { percent: 96, eta: 'Image ready · loading your preview' };
    if (phase === 'check') return { percent: 88, eta: 'Checking your existing request' };
    const percent = phase === 'prepare' ? Math.min(12, 2 + elapsed / 2) : Math.min(92, 15 + 78 * (1 - Math.exp(-elapsed / 42)));
    if (elapsed >= 90) return { percent, eta: 'Longer than estimated · still working' };
    if (phase === 'prepare') return { percent, eta: 'Estimated total: 30–90 seconds' };
    const lower = Math.max(5, Math.ceil((30 - elapsed) / 5) * 5);
    const upper = Math.max(lower, Math.ceil((90 - elapsed) / 5) * 5);
    return { percent, eta: `Estimated wait: about ${lower === upper ? upper : `${lower}–${upper}`} seconds` };
  }

  function initProduct(root) {
    if (instances.has(root)) return;
    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => [...root.querySelectorAll(selector)];
    const form = q('.ftp-form');
    let variants;
    try { variants = JSON.parse(q('[data-ft-variant-data]').textContent); } catch (_error) { return; }
    if (!form || !Array.isArray(variants)) return;
    const controller = new AbortController();
    const listen = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal: controller.signal });
    const purchaseEnabled = root.dataset.purchaseEnabled === 'true';
    const quoteOnly = root.dataset.quoteOnly === 'true';
    const isKit = root.dataset.category === 'diy';
    const variantSelect = q('[data-ft-variant-select]');
    const optionGroups = qa('[data-ft-option-group]');
    const largerGroup = q('[data-ft-larger-group]');
    const photoInputs = qa('[data-ft-photo-input]');
    const panels = qa('[data-ft-step-panel]');
    const progress = q('[data-ft-progress]');
    const nextButton = q('[data-ft-next]');
    const prevButton = q('[data-ft-prev]');
    const consent = q('[data-ft-photo-consent]');
    const aiFileInput = q('[data-ft-ai-file]');
    const wizardEnabled = panels.length === 3 && Boolean(progress && nextButton && prevButton);
    const initialNoValidate = form.noValidate;
    const objectUrls = new Map();
    const fileChecks = new Map();
    let endpoint = '';
    try {
      const parsed = new URL(root.dataset.aiEndpoint || '');
      if (parsed.protocol === 'https:' && !parsed.username && !parsed.password) endpoint = parsed.origin;
    } catch (_error) { /* Manual design remains available when the service is unconfigured. */ }
    const aiEnabled = root.dataset.aiEnabled === 'true' && Boolean(endpoint) && typeof DataTransfer === 'function' && Boolean(window.crypto?.getRandomValues);
    const sessionKey = `famitiny-ai-session:${endpoint}`;
    let session = null;
    try { session = JSON.parse(sessionStorage.getItem(sessionKey) || 'null'); } catch (_error) { /* Storage is optional. */ }
    let sessionPromise = null;
    let selectedVariant = variants.find((variant) => String(variant.id) === variantSelect?.value) || variants[0];
    let currentStep = 1;
    let submitting = false;
    let busy = false;
    let restoring = false;
    let submitDestination = 'checkout';
    let generationVersion = 0;
    let localGenerations = 0;
    let maxGenerations = 3;
    let generationClock = null;
    let generationTimer = null;
    function applyLimits(result) {
      const limits = result?.limits;
      if (!limits) return;
      if (Number.isFinite(Number(limits.perSession))) maxGenerations = Math.max(1, Number(limits.perSession));
      if (limits.remainingSession !== null && limits.remainingSession !== undefined) localGenerations = Math.max(localGenerations, maxGenerations - Number(limits.remainingSession));
    }
    let preview = { status: 'none', accepted: false, manual: false, id: '', token: '', url: '', blob: null, owner: false };
    const previewHistory = [];
    const hasOwnerSession = () => preview.owner && session?.token && new Date(session.expiresAt).getTime() > Date.now() + 30000;
    const remainingPreviews = () => Math.max(0, maxGenerations - localGenerations);

    const specialQuote = () => quoteOnly || Boolean(largerGroup?.checked);
    const purchasing = () => purchaseEnabled && !specialQuote();
    const files = () => photoInputs.flatMap((input) => input.files?.[0] ? [input.files[0]] : []);
    const value = (selector) => q(selector)?.value?.trim() || '';
    const text = (selector, content) => qa(selector).forEach((element) => { element.textContent = content; });
    const show = (selector, visible) => qa(selector).forEach((element) => { element.hidden = !visible; });
    const error = (selector, message) => { text(selector, message); show(selector, Boolean(message)); };
    function renderGenerationProgress() {
      if (!generationClock) return;
      const seconds = Math.floor(Math.max(0, performance.now() - generationClock.startedAt) / 1000);
      const estimate = generationEstimate(seconds, generationClock.phase);
      generationClock.percent = Math.max(generationClock.percent, estimate.percent);
      const elapsed = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} elapsed`;
      text('[data-ft-generation-elapsed]', elapsed);
      text('[data-ft-generation-eta]', estimate.eta);
      const track = q('[data-ft-generation-track]');
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(generationClock.percent)));
        track.setAttribute('aria-valuetext', `${elapsed}. ${estimate.eta}. Progress is estimated.`);
      }
      const fill = q('[data-ft-generation-fill]');
      if (fill) fill.style.width = `${generationClock.percent}%`;
    }
    function setGenerationPhase(phase) {
      if (!generationClock) return;
      generationClock.phase = phase;
      const copy = {
        prepare: ['Preparing your photos…', 'Keep this page open. The bar and wait time are estimates; generation speed can vary.'],
        create: ['Creating your little someone…', 'Your request has been sent. Photos upload first, then your image is created. The bar and wait time are estimates.'],
        check: ['Checking on your little someone…', 'The connection is taking longer. We’re checking the same request, without using another preview.'],
        finish: ['Bringing your preview into view…', 'Your image has been created. Loading it for you now.'],
        ready: ['Your little someone is ready.', 'Your preview is ready to review.'],
      };
      text('[data-ft-generation-title]', copy[phase][0]);
      text('[data-ft-generation-notice]', copy[phase][1]);
      const current = phase === 'prepare' ? 0 : ['finish', 'ready'].includes(phase) ? 2 : 1;
      qa('[data-ft-generation-stage]').forEach((item, index) => {
        item.classList.toggle('is-current', phase !== 'ready' && index === current);
        item.classList.toggle('is-complete', phase === 'ready' || index < current);
        if (phase !== 'ready' && index === current) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      });
      renderGenerationProgress();
    }
    function startGenerationProgress(checkExisting = false) {
      clearInterval(generationTimer);
      if (!checkExisting || !generationClock) generationClock = { startedAt: performance.now(), phase: 'prepare', percent: 0 };
      setGenerationPhase(checkExisting ? 'check' : 'prepare');
      generationTimer = setInterval(renderGenerationProgress, 1000);
    }
    function stopGenerationProgress() { clearInterval(generationTimer); generationTimer = null; }
    const choices = () => {
      const result = [];
      optionGroups.forEach((group) => { result[Number(group.dataset.ftOptionGroup)] = group.querySelector('input:checked')?.value || ''; });
      return result;
    };
    const style = () => q('[data-ft-style]:checked')?.dataset.ftStyle || 'soft-cartoon';
    const composition = () => isKit ? (value('[data-ft-kit-subject]:checked') === 'A pet' ? '1 pet' : '1 person') : (specialQuote() ? '3 or more people' : choices()[0] || '1 person');
    const config = () => ({
      category: root.dataset.category,
      composition: composition(),
      style: style(),
      notes: value('[data-ft-notes]'),
      baseText: value('[data-ft-base-text]'),
    });
    function summary() {
      if (specialQuote()) return `3 or more people · personal quote`;
      const selection = selectedVariant?.title || choices().join(' / ');
      return isKit ? `${value('[data-ft-kit-subject]:checked') || 'One person'} · complete kit · ${selectedVariant?.options?.find((item) => /cm/.test(item)) || ''}` : selection;
    }
    function privateLink() {
      if (!preview.id || !preview.token) return '';
      const url = new URL(root.dataset.productUrl, window.location.origin);
      if (selectedVariant) url.searchParams.set('variant', String(selectedVariant.id));
      url.hash = `preview=${preview.id}.${preview.token}`;
      return url.href;
    }
    function clearPreviewHash() {
      if (window.location.hash.startsWith('#preview=')) {
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState(null, '', url.href);
      }
    }
    function setOrderPreview() {
      const approved = preview.accepted && preview.blob;
      if (aiFileInput) {
        if (approved) {
          const transfer = new DataTransfer();
          const extension = preview.blob.type === 'image/png' ? 'png' : 'jpg';
          transfer.items.add(new File([preview.blob], `famitiny-concept-${preview.id}.${extension}`, { type: preview.blob.type }));
          aiFileInput.files = transfer.files;
        } else aiFileInput.value = '';
      }
      q('[data-ft-preview-id]').value = approved ? preview.id : '';
      q('[data-ft-preview-model]').value = approved ? (preview.model || 'AI figurine preview') : '';
      q('[data-ft-design-approval]').value = approved ? 'AI concept selected; final design confirmation required before printing' : 'Designer-led confirmation requested';
      q('[data-ft-change-property]').value = approved ? (preview.changeRequest || '') : '';
    }
    function invalidatePreview(message = '') {
      if (restoring) return;
      generationVersion += 1;
      releasePreviewImages();
      preview = { status: 'none', accepted: false, manual: false, id: '', token: '', url: '', blob: null, owner: false };
      if (q('[data-ft-preview-change]')) q('[data-ft-preview-change]').value = '';
      setOrderPreview();
      clearPreviewHash();
      text('[data-ft-preview-status]', message);
      error('[data-ft-preview-error]', '');
      updatePreview();
    }
    function rememberPreview(item) {
      if (item.status !== 'ready' || !item.url || previewHistory.some((entry) => entry.id === item.id)) return;
      previewHistory.push({ ...item, pendingId: '', pendingChangeRequest: '', accepted: false });
    }
    function releasePreviewImages() {
      new Set([preview.url, ...previewHistory.map((entry) => entry.url)].filter(Boolean)).forEach((url) => URL.revokeObjectURL(url));
      previewHistory.length = 0;
    }
    function updateVersionPicker() {
      const list = q('[data-ft-preview-versions]');
      if (!list) return;
      show('[data-ft-preview-history]', previewHistory.length > 1 && preview.status === 'ready');
      // Keep the existing buttons while typing, so keyboard focus stays in place.
      const signature = previewHistory.map((entry) => entry.id).join('|');
      if (list.dataset.versions !== signature) {
        list.replaceChildren(); list.dataset.versions = signature;
        previewHistory.forEach((entry, index) => {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'ftp-preview-version'; button.dataset.ftPreviewVersion = entry.id;
          const image = document.createElement('img'); image.src = entry.url; image.alt = ''; image.width = 64; image.height = 76;
          const label = document.createElement('span'); label.textContent = `Version ${index + 1}`;
          button.append(image, label); list.append(button);
        });
      }
      list.querySelectorAll('[data-ft-preview-version]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.ftPreviewVersion === preview.id));
        button.disabled = busy || submitting || restoring || Boolean(preview.pendingId);
      });
    }
    function updateInquiry() {
      const link = q('[data-ft-inquiry]');
      if (!link) return;
      const body = ['Hello Famitiny,', '', `I would like to discuss: ${root.dataset.productTitle}.`, `My choices: ${summary()}.`, `Style: ${value('[data-ft-style]:checked') || 'Soft cartoon'}.`];
      if (!specialQuote() && selectedVariant) body.push(`Website reference price: ${selectedVariant.priceText}.`);
      if (value('[data-ft-base-text]')) body.push(`Requested base text: ${value('[data-ft-base-text]')}`);
      if (value('[data-ft-notes]')) body.push(`Details: ${value('[data-ft-notes]')}`);
      if (preview.status === 'ready') body.push(`My preview: ${privateLink()}`);
      body.push('', 'Delivery country:', 'Preferred date, if any:', '', 'I will attach my reference photos to this email. Please confirm design, price and timing before payment.');
      link.href = `mailto:${root.dataset.contactEmail}?subject=${encodeURIComponent(`My Famitiny design: ${root.dataset.productTitle}`)}&body=${encodeURIComponent(body.join('\n'))}`;
    }
    function updateReview() {
      text('[data-ft-review-selection]', summary());
      text('[data-ft-review-style]', value('[data-ft-style]:checked') || 'Soft cartoon');
      text('[data-ft-review-base]', value('[data-ft-base-text]') || 'No message added');
      text('[data-ft-review-notes]', [value('[data-ft-notes]'), preview.accepted && preview.changeRequest ? `Selected preview adjustment: ${preview.changeRequest}` : ''].filter(Boolean).join('\n') || 'No extra details added');
      text('[data-ft-review-photo-count]', files().length ? `${files().length} reference photo${files().length === 1 ? '' : 's'}` : 'Attach photos to your inquiry email');
      const gallery = q('[data-ft-review-photos]');
      gallery.replaceChildren();
      photoInputs.forEach((input, index) => {
        if (!input.files?.[0] || !objectUrls.has(input)) return;
        const figure = document.createElement('figure');
        figure.className = 'ftp-review-photo';
        const image = document.createElement('img');
        image.src = objectUrls.get(input); image.alt = `Your reference photo ${index + 1}`; image.width = 120; image.height = 120;
        const caption = document.createElement('figcaption'); caption.textContent = input.files[0].name;
        figure.append(image, caption); gallery.append(figure);
      });
      show('[data-ft-approved-preview]', Boolean(preview.accepted && preview.url));
      show('[data-ft-designer-review]', !preview.accepted);
      if (preview.accepted && preview.url) q('[data-ft-approved-image]').src = preview.url;
    }
    function updateActions() {
      const finalStep = !wizardEnabled || currentStep === 3;
      const canBuy = purchasing() && Boolean(selectedVariant?.available);
      const locked = busy || submitting || restoring;
      const buy = q('[data-ft-add-to-cart]');
      if (buy) { buy.hidden = !purchasing() || !finalStep; buy.disabled = !canBuy || locked; }
      text('[data-ft-add-label]', submitting && submitDestination === 'checkout' ? 'Opening checkout…' : canBuy ? 'Checkout securely' : 'Currently unavailable');
      const bag = q('[data-ft-add-to-bag]');
      if (bag) { bag.hidden = !purchasing() || !finalStep || !wizardEnabled; bag.disabled = !canBuy || locked; bag.textContent = submitting && submitDestination === 'cart' ? 'Adding to bag…' : 'Add to bag instead'; }
      show('[data-ft-inquiry]', !purchasing() && finalStep);
      nextButton.hidden = !wizardEnabled || finalStep;
      nextButton.disabled = locked || (currentStep === 2 && aiEnabled && !specialQuote() && preview.status !== 'ready' && !preview.manual);
      text('[data-ft-next-label]', currentStep === 1 ? (aiEnabled && !specialQuote() ? 'Create my free preview' : 'Continue to design') : preview.status === 'ready' ? 'Love this · choose a size' : specialQuote() ? 'Review my request' : 'Choose a size');
      prevButton.hidden = !wizardEnabled || currentStep === 1;
      prevButton.disabled = locked;
      qa('.ftp-progress-button').forEach((button) => {
        const step = Number(button.dataset.ftStepTarget);
        button.classList.toggle('is-current', step === currentStep); button.classList.toggle('is-complete', step < currentStep);
        if (step === currentStep) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
        button.disabled = locked;
      });
      qa('[data-ft-generate-preview], [data-ft-regenerate], [data-ft-manual-preview], [data-ft-delete-preview], [data-ft-check-preview]').forEach((button) => { button.disabled = locked; });
      const revisionLocked = locked || !hasOwnerSession() || !remainingPreviews() || Boolean(preview.pendingId);
      q('[data-ft-regenerate]').disabled = revisionLocked;
      qa('[data-ft-preview-change], [data-ft-change-suggestion]').forEach((control) => { control.disabled = revisionLocked; });
      updateVersionPicker();
      // Freeze appearance controls during a generation so its result cannot attach to changed inputs.
      qa('[data-ft-step-panel="1"] input:not([name="id"]), [data-ft-step-panel="1"] textarea, [data-ft-photo-remove]').forEach((input) => {
        if (input.matches('[data-ft-option]')) return;
        input.disabled = busy || restoring;
      });
      optionGroups.forEach((group) => {
        const index = Number(group.dataset.ftOptionGroup); const selected = choices();
        group.querySelectorAll('[data-ft-option]').forEach((input) => {
          const matches = variants.filter((variant) => variant.options[index] === input.value && variant.options.every((option, position) => position === index || option === selected[position]));
          input.disabled = locked || !matches.length || (purchasing() && !matches.some((variant) => variant.available));
        });
      });
    }
    function showStep(step, focus = true) {
      if (!wizardEnabled) return;
      currentStep = Math.max(1, Math.min(3, step)); root.dataset.currentStep = String(currentStep);
      panels.forEach((panel) => { panel.hidden = Number(panel.dataset.ftStepPanel) !== currentStep; });
      text('[data-ft-step-status]', `Step ${currentStep} of 3: ${['Your photos', 'Your preview', 'Size and checkout'][currentStep - 1]}`);
      updateReview(); updateActions();
      if (focus) {
        panels.find((panel) => Number(panel.dataset.ftStepPanel) === currentStep)?.querySelector('.ftp-panel-title')?.focus({ preventScroll: true });
        progress.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }
    function updatePrices() {
      const special = specialQuote(); const valid = Boolean(selectedVariant);
      text('[data-ft-price]', special ? 'Made for your idea' : valid ? selectedVariant.priceText : 'Let’s discuss your idea');
      text('[data-ft-price-caption]', special || !valid ? 'Personal quote' : isKit ? 'Price for your complete kit' : 'Price for your selected figurine');
      text('[data-ft-summary]', summary()); text('[data-ft-summary-price]', special || !valid ? 'Personal quote' : selectedVariant.priceText);
      text('[data-ft-action-caption]', purchasing() ? 'Free shipping to the US, UK & EU' : 'Price confirmed by email');
      show('[data-ft-email-help]', !purchasing()); show('[data-ft-shipping-note], [data-ft-review-shipping], [data-ft-review-cart-note]', purchasing());
      text('[data-ft-order-note]', purchasing() ? 'Your photos and selected concept will travel with your order. We’ll confirm the final design before printing. Applicable taxes and any eligible discount are shown at checkout.' : 'We’ll confirm your design, final price and delivery timing before you pay.');
      show('[data-ft-size-options]', !special);
      if (variantSelect) variantSelect.value = selectedVariant ? String(selectedVariant.id) : '';
      const selected = choices();
      optionGroups.forEach((group) => {
        const index = Number(group.dataset.ftOptionGroup);
        group.querySelector('[data-ft-option-value]').textContent = selected[index] || '';
        if (group.dataset.ftOptionName === 'Size') group.querySelectorAll('[data-ft-option]').forEach((input) => {
          const match = variants.find((variant) => variant.options[index] === input.value && variant.options.every((option, position) => position === index || option === selected[position]));
          const price = input.closest('label').querySelector('[data-ft-size-price]');
          if (price) price.textContent = special ? 'Personal quote' : match?.priceText || 'Unavailable';
        });
      });
      updateInquiry(); updateReview(); updatePreview(); updateActions();
    }
    function updateOptions() {
      const selected = choices();
      if (!quoteOnly) selectedVariant = variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
      updatePrices();
    }
    function updatePreview() {
      const ready = preview.status === 'ready' && Boolean(preview.url);
      const service = aiEnabled && !specialQuote();
      show('[data-ft-preview-loading]', busy);
      show('[data-ft-preview-empty]', !busy && !ready);
      show('[data-ft-preview-comparison]', ready);
      show('[data-ft-preview-tools], [data-ft-preview-link-help]', ready && !busy);
      show('[data-ft-preview-changes]', ready);
      show('[data-ft-delete-preview]', ready && Boolean(hasOwnerSession()));
      show('[data-ft-preview-readonly-help]', ready && !hasOwnerSession());
      show('[data-ft-generate-preview]', service && !ready && !busy && !preview.pendingId && localGenerations < maxGenerations);
      show('[data-ft-check-preview]', Boolean(preview.pendingId) && !busy);
      show('[data-ft-preview-limit]', service);
      const remaining = remainingPreviews();
      text('[data-ft-preview-limit]', `${remaining} of ${maxGenerations} previews left in this session. Creating or revising an image uses one preview. Daily availability also applies.`);
      text('[data-ft-revision-allowance]', !hasOwnerSession() ? 'This saved preview is read-only. To make a new design, return to Your photos and change or re-upload a photo.' : preview.pendingId ? 'A revision may still be running. Check its status below before making another change.' : remaining ? `Each revision uses 1 preview. ${remaining} remaining in this session.` : 'No previews left in this session. Keep a version you like, or continue with a designer.');
      text('[data-ft-revision-count]', `${value('[data-ft-preview-change]').length}/300`);
      text('[data-ft-regenerate]', busy ? 'Working on your changes…' : 'Update this preview');
      text('[data-ft-manual-copy]', specialQuote() ? 'Larger groups and special ideas need a personal quote. We’ll work through the details together.' : !aiEnabled ? 'Our designer will work with your photos and confirm the appearance with you before printing.' : 'Prefer to work through the details together? Our designer can help.');
      text('[data-ft-manual-preview]', specialQuote() ? 'Continue to my quote request' : 'Continue with a designer');
      text('[data-ft-preview-intro]', specialQuote() ? 'Your family deserves a design made around everyone. We’ll confirm the arrangement and price personally.' : 'A little glimpse of what your keepsake could become.');
      if (ready) {
        q('[data-ft-preview-image]').src = preview.url;
        const first = photoInputs.find((input) => objectUrls.has(input));
        if (first) q('[data-ft-preview-before]').src = objectUrls.get(first);
        q('[data-ft-download-preview]').href = preview.url;
      }
      updateActions();
    }
    function clearPhoto(input) {
      if (objectUrls.has(input)) URL.revokeObjectURL(objectUrls.get(input));
      objectUrls.delete(input); fileChecks.delete(input); input.value = ''; input.removeAttribute('tabindex');
      const slot = input.closest('[data-ft-photo-slot]'); slot.querySelector('[data-ft-photo-thumb]').removeAttribute('src'); slot.querySelector('[data-ft-photo-name]').textContent = ''; slot.querySelector('.ftp-photo-preview').hidden = true; slot.classList.remove('has-photo');
    }
    function updatePhotoSlots() {
      let emptyShown = false;
      photoInputs.forEach((input) => {
        const slot = input.closest('[data-ft-photo-slot]');
        if (input.files?.length) slot.hidden = false; else { slot.hidden = emptyShown; emptyShown = true; }
      });
      const count = files().length;
      text('[data-ft-photo-status]', count ? `${count} reference photo${count === 1 ? '' : 's'} selected. ${aiEnabled ? 'Creating a preview sends these photos to our image service. Your order also includes your reference photos.' : 'Photos are sent with your order, or attached by you to an inquiry email.'}` : 'Add a clear reference photo to begin.');
      if (consent) consent.required = purchasing() || count > 0;
      if (photoInputs[0]) photoInputs[0].required = purchasing() && count === 0;
      updateInquiry(); updateReview();
    }
    async function validImage(file) {
      if (!ALLOWED_TYPES.has(file.type)) return false;
      const h = new Uint8Array(await file.slice(0, 12).arrayBuffer());
      return (file.type === 'image/jpeg' && h[0] === 255 && h[1] === 216 && h[2] === 255) || (file.type === 'image/png' && h[0] === 137 && h[1] === 80 && h[2] === 78 && h[3] === 71 && h[4] === 13 && h[5] === 10 && h[6] === 26 && h[7] === 10) || (file.type === 'image/webp' && h[0] === 82 && h[1] === 73 && h[2] === 70 && h[3] === 70 && h[8] === 87 && h[9] === 69 && h[10] === 66 && h[11] === 80);
    }
    async function handlePhoto(input, invalidate = true) {
      if (invalidate) invalidatePreview('Your photos changed. Create a fresh preview, or continue with a designer.');
      if (objectUrls.has(input)) URL.revokeObjectURL(objectUrls.get(input));
      objectUrls.delete(input); fileChecks.delete(input);
      const file = input.files?.[0];
      if (!file) { clearPhoto(input); updatePhotoSlots(); return; }
      const token = {}; fileChecks.set(input, token);
      let problem = '';
      if (file.size > MAX_FILE_BYTES) problem = 'Please choose a photo smaller than 10 MB.';
      else if (files().reduce((sum, item) => sum + item.size, 0) > MAX_TOTAL_BYTES) problem = 'Your photos exceed 25 MB together. Please use smaller images.';
      else { try { if (!(await validImage(file))) problem = 'Please choose a valid JPG, PNG or WebP image.'; } catch (_error) { problem = 'We couldn’t read this photo. Please choose it again.'; } }
      if (fileChecks.get(input) !== token || input.files?.[0] !== file) return;
      fileChecks.delete(input);
      if (problem) { clearPhoto(input); error('[data-ft-file-error]', problem); showStep(1, false); updatePhotoSlots(); return; }
      error('[data-ft-file-error]', ''); error('[data-ft-submit-error]', '');
      const url = URL.createObjectURL(file); objectUrls.set(input, url);
      const slot = input.closest('[data-ft-photo-slot]'); slot.querySelector('[data-ft-photo-thumb]').src = url; slot.querySelector('[data-ft-photo-name]').textContent = file.name; slot.querySelector('.ftp-photo-preview').hidden = false; slot.classList.add('has-photo'); input.tabIndex = -1;
      updatePhotoSlots();
    }
    function validateDetails(requirePhoto = purchasing()) {
      error('[data-ft-submit-error]', '');
      if (fileChecks.size) { showStep(1); error('[data-ft-submit-error]', 'Please wait while your photos finish checking.'); return false; }
      if (requirePhoto && !files().length) { showStep(1); error('[data-ft-submit-error]', 'Please choose at least one reference photo.'); photoInputs.find((input) => !input.closest('[data-ft-photo-slot]').hidden)?.focus(); return false; }
      if ((requirePhoto || files().length) && !consent?.checked) { showStep(1); error('[data-ft-submit-error]', 'Please confirm permission to share these photos for your custom design and preview.'); consent?.focus(); return false; }
      for (const input of qa('[data-ft-base-text], [data-ft-notes]')) if (!input.checkValidity()) { showStep(1); input.closest('details').open = true; input.reportValidity(); return false; }
      return true;
    }
    function validateVariant() {
      if (purchasing() && (!selectedVariant || !selectedVariant.available)) { showStep(3); error('[data-ft-submit-error]', 'Please choose an available size and composition.'); return false; }
      return true;
    }
    function approvePreview() {
      if (preview.status !== 'ready' || !preview.blob) return false;
      preview.accepted = true; preview.manual = false;
      try { setOrderPreview(); } catch (_error) { preview.accepted = false; error('[data-ft-preview-error]', 'This browser couldn’t attach the concept to your order. Continue with a designer or use another browser.'); return false; }
      return true;
    }
    function goToStep(step) {
      if (busy || submitting || restoring) return;
      if (step > 1 && !validateDetails()) return;
      if (step === 3 && !preview.manual && !preview.accepted) {
        if (preview.status === 'ready') { if (!approvePreview()) return; }
        else if (!aiEnabled || specialQuote()) { preview.manual = true; setOrderPreview(); }
        else { showStep(2); error('[data-ft-preview-error]', 'Create and select a preview, or choose “Continue with a designer”.'); return; }
      }
      showStep(step);
    }

    async function api(path, { method = 'GET', token, body, timeout = 115000 } = {}) {
      const requestController = new AbortController();
      const abort = () => requestController.abort();
      controller.signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, timeout);
      try {
        return await fetch(`${endpoint}${path}`, { method, headers: token ? { Authorization: `Bearer ${token}` } : {}, body, signal: requestController.signal, credentials: 'omit', cache: 'no-store' });
      } finally { clearTimeout(timer); controller.signal.removeEventListener('abort', abort); }
    }
    async function jsonResponse(response) {
      let body;
      try { body = await response.json(); } catch (_error) { throw new Error('The preview service returned an unreadable response. Your photos are still here.'); }
      if (!response.ok) { const failure = new Error(body?.error?.message || body?.message || 'The preview service is unavailable. You can continue with a designer.'); failure.status = response.status; failure.code = body?.error?.code; applyLimits(body); throw failure; }
      return body;
    }
    async function getSession() {
      if (session?.token && new Date(session.expiresAt).getTime() > Date.now() + 30000) return session;
      if (sessionPromise) return sessionPromise;
      sessionPromise = (async () => {
        session = await jsonResponse(await api('/api/session', { timeout: 20000 }));
        if (!session.token) throw new Error('We couldn’t start your private preview session. Please try again later.');
        session.readyAt = Date.now() + 2100;
        applyLimits(session);
        try { sessionStorage.setItem(sessionKey, JSON.stringify(session)); } catch (_error) { /* This page still works without persistent storage. */ }
        return session;
      })();
      try { return await sessionPromise; } finally { sessionPromise = null; }
    }
    function uuid() {
      if (crypto.randomUUID) return crypto.randomUUID();
      const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
      const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    async function preparePhoto(file, index) {
      const image = new Image(); const objectUrl = URL.createObjectURL(file);
      try {
        await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = () => reject(new Error('One photo could not be prepared. Please choose a different image.')); image.src = objectUrl; });
        if (!image.naturalWidth || !image.naturalHeight) throw new Error('Please choose a clear image with valid dimensions.');
        const ratio = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas'); canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio)); canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const context = canvas.getContext('2d'); if (!context) throw new Error('This browser could not prepare your photo. Continue with a designer.');
        context.fillStyle = '#ffffff'; context.fillRect(0, 0, canvas.width, canvas.height); context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const encode = (quality) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
        let blob = await encode(0.88);
        if (blob && blob.size > 1.5 * 1024 * 1024) blob = await encode(0.72);
        if (!blob) throw new Error('This browser could not prepare your photo. Continue with a designer.');
        return new File([blob], `reference-${index + 1}.jpg`, { type: 'image/jpeg' });
      } finally { URL.revokeObjectURL(objectUrl); }
    }
    async function imageBlob(path, token) {
      const response = await api(path, { token, timeout: 30000 });
      if (!response.ok) throw new Error('Your preview image could not be loaded. Try opening your private link again.');
      const blob = await response.blob();
      if (!ALLOWED_TYPES.has(blob.type.split(';')[0]) || blob.size > MAX_FILE_BYTES) throw new Error('The preview service returned an unsupported image. Continue with a designer.');
      return blob;
    }
    async function installReady(result, token, owner, version, changeRequest = '') {
      if (!result.id || !result.previewToken) throw new Error('The preview is missing its private reference. Please continue with a designer.');
      applyLimits(result);
      if (busy) setGenerationPhase('finish');
      const blob = await imageBlob(`/api/previews/${encodeURIComponent(result.id)}/image`, token);
      if (version !== generationVersion) return;
      rememberPreview(preview);
      preview = { status: 'ready', accepted: false, manual: false, id: result.id, token: result.previewToken, url: URL.createObjectURL(blob), blob, owner, model: result.model || result.config?.model, changeRequest: result.revisionNotes || changeRequest, sourcePreviewId: result.sourcePreviewId || '', revisionNumber: result.revisionNumber || 0, config: result.config || result };
      rememberPreview(preview);
      setOrderPreview(); text('[data-ft-preview-status]', result.sourcePreviewId ? 'Your revised preview is ready. Compare your versions, then choose the one you love.' : 'A little you, ready to meet. Keep this concept, or tell us what to adjust.');
      error('[data-ft-preview-error]', ''); updateInquiry(); updateReview(); updatePreview();
      if (busy) setGenerationPhase('ready');
      const link = privateLink();
      if (link) window.history.replaceState(null, '', link);
    }
    async function pollPreview(id, token, version) {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (version !== generationVersion || controller.signal.aborted) return null;
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const response = await api(`/api/previews/${encodeURIComponent(id)}`, { token, timeout: 15000 });
        const result = await jsonResponse(response);
        if (result.status === 'ready') return result;
        if (result.status === 'failed') { const failure = new Error(result.error?.message || 'This preview couldn’t be created. Please try different photos or continue with a designer.'); failure.known = true; throw failure; }
      }
      return null;
    }
    async function generate(changeRequest = '') {
      if (!aiEnabled || specialQuote() || busy || submitting || restoring) return;
      const revising = Boolean(changeRequest);
      if (revising && (preview.status !== 'ready' || !hasOwnerSession())) { error('[data-ft-preview-error]', 'This preview is read-only or its editing session has expired. Keep this concept, continue with a designer, or return to Your photos to start a new design.'); updatePreview(); return; }
      if (revising && (changeRequest.length > 300 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(changeRequest))) { error('[data-ft-preview-error]', 'Please describe your changes in 300 characters or fewer.'); q('[data-ft-preview-change]').focus(); return; }
      if (!validateDetails(true)) return;
      if (preview.pendingId) { showStep(2); error('[data-ft-preview-error]', 'Your previous request may still be running. Check its status before starting another preview.'); return; }
      if (localGenerations >= maxGenerations) { showStep(2); error('[data-ft-preview-error]', 'You’ve reached this session’s preview limit. You can keep your latest concept or continue with a designer.'); return; }
      const previous = { ...preview };
      const requestConfig = config();
      generationVersion += 1; const version = generationVersion;
      busy = true; preview.accepted = false; preview.manual = false; setOrderPreview();
      startGenerationProgress();
      error('[data-ft-preview-error]', ''); text('[data-ft-preview-status]', ''); showStep(2); updatePreview();
      let requestId = '';
      try {
        // A revision must stay in its original owner session. Never silently
        // create a different session and use it to edit a saved read-only image.
        const activeSession = revising ? session : await getSession();
        const prepared = [];
        if (!revising) for (const [index, file] of files().entries()) prepared.push(await preparePhoto(file, index));
        if (prepared.reduce((sum, file) => sum + file.size, 0) > MAX_AI_BYTES) throw new Error('These photos are still too large for a preview. Use fewer photos or smaller images.');
        if (version !== generationVersion) return;
        requestId = uuid();
        const body = new FormData(); body.set('requestId', requestId); body.set('consent', 'true'); body.set('website', '');
        if (revising) body.set('revisionNotes', changeRequest);
        else {
          Object.entries(requestConfig).forEach(([key, content]) => body.set(key, content));
          prepared.forEach((file) => body.append('photos', file));
        }
        if (activeSession.readyAt > Date.now()) await new Promise((resolve) => setTimeout(resolve, activeSession.readyAt - Date.now()));
        localGenerations += 1;
        setGenerationPhase('create');
        if (revising) { text('[data-ft-generation-title]', 'Making your little adjustments…'); text('[data-ft-generation-notice]', 'We’re editing the preview shown below. Your earlier version stays available while we work.'); }
        let result;
        const path = revising ? `/api/previews/${encodeURIComponent(previous.id)}/revisions` : '/api/previews';
        try { result = await jsonResponse(await api(path, { method: 'POST', token: activeSession.token, body, timeout: 215000 })); }
        catch (failure) {
          if (failure.status && failure.status < 500) throw failure;
          setGenerationPhase('check');
          text('[data-ft-preview-status]', 'Your request may still be working. Checking its progress without starting another generation…');
          result = await pollPreview(requestId, activeSession.token, version);
        }
        if (result?.status === 'pending' || result?.status === 'processing') result = await pollPreview(requestId, activeSession.token, version);
        applyLimits(result);
        if (result?.status === 'failed') { const failure = new Error(result.error?.message || 'The preview could not be created. Continue with a designer or try different photos.'); failure.known = true; throw failure; }
        if (!result || result.status !== 'ready') throw new Error('The preview is taking longer than expected. Please keep this page open and choose “Check preview status”, or continue with a designer.');
        await installReady(result, activeSession.token, true, version, changeRequest);
        q('[data-ft-preview-change]').value = '';
      } catch (failure) {
        if (version !== generationVersion) return;
        if (previous.status === 'ready') { preview = previous; setOrderPreview(); updateReview(); }
        else preview.status = 'failed';
        if (revising && (failure.status === 401 || failure.code === 'preview_not_owned' || failure.code === 'read_only_preview')) preview.owner = false;
        if (requestId && !failure.known && (!failure.status || failure.status >= 500)) { preview.pendingId = requestId; preview.pendingChangeRequest = changeRequest; }
        error('[data-ft-preview-error]', `${failure.message || 'We couldn’t create this preview. Your photos are still here; continue with a designer.'}${revising && previous.status === 'ready' ? ' Your previous preview is still available.' : ''}`);
      } finally {
        if (version === generationVersion) { stopGenerationProgress(); busy = false; updatePreview(); updateActions(); }
      }
    }
    async function restoreFromLink() {
      if (!aiEnabled || !window.location.hash.startsWith('#preview=')) return;
      const match = window.location.hash.slice(9).match(/^([a-f0-9-]{36})\.(.+)$/i);
      if (!match) return;
      const [, id, readToken] = match;
      restoring = true; generationVersion += 1; const version = generationVersion;
      text('[data-ft-resume-status]', 'Opening your private preview and reference photos…'); show('[data-ft-resume-status]', true); updateActions();
      try {
        let auth = readToken; let owner = false; let result;
        if (session?.token && new Date(session.expiresAt).getTime() > Date.now()) {
          const ownResponse = await api(`/api/previews/${encodeURIComponent(id)}`, { token: session.token, timeout: 20000 });
          if (ownResponse.ok) { result = await ownResponse.json(); auth = session.token; owner = true; }
        }
        if (!result) result = await jsonResponse(await api(`/api/previews/${encodeURIComponent(id)}`, { token: readToken, timeout: 20000 }));
        if (result.status !== 'ready') throw new Error('This private preview is not ready. You can create a fresh preview or continue with a designer.');
        const saved = result.config || result;
        if (saved.category !== root.dataset.category) throw new Error('This preview belongs to a different keepsake. Please open the original product link.');
        const restored = [];
        for (const photo of (result.photos || []).slice(0, 5)) {
          const blob = await imageBlob(`/api/previews/${encodeURIComponent(id)}/photos/${photo.index}`, auth);
          restored.push(new File([blob], photo.name || `reference-${restored.length + 1}.jpg`, { type: blob.type }));
        }
        if (!restored.length) throw new Error('The reference photos are unavailable. Please add your photos again before ordering.');
        photoInputs.forEach(clearPhoto);
        restored.forEach((file, index) => { const transfer = new DataTransfer(); transfer.items.add(file); photoInputs[index].files = transfer.files; });
        for (const input of photoInputs) if (input.files?.length) await handlePhoto(input, false);
        optionGroups.filter((group) => group.dataset.ftOptionName !== 'Size').forEach((group) => {
          group.querySelectorAll('[data-ft-option]').forEach((input) => { if (input.value === saved.composition) input.checked = true; });
        });
        if (isKit) qa('[data-ft-kit-subject]').forEach((input) => { input.checked = input.value === (saved.composition === '1 pet' ? 'A pet' : 'One person'); });
        qa('[data-ft-style]').forEach((input) => { input.checked = input.dataset.ftStyle === saved.style; });
        q('[data-ft-notes]').value = String(saved.notes || '').slice(0, 600); q('[data-ft-base-text]').value = String(saved.baseText || '').slice(0, 32);
        if (consent) consent.checked = false;
        updateOptions();
        await installReady({ ...result, previewToken: result.previewToken || readToken }, auth, owner, version);
        text('[data-ft-resume-status]', 'Your saved preview is ready. Review it below, then choose your size.');
        showStep(2, false);
      } catch (failure) { error('[data-ft-resume-status]', failure.message || 'This preview link could not be opened. Please create a new preview.'); clearPreviewHash(); }
      finally { restoring = false; updateActions(); }
    }

    listen(nextButton, 'click', () => {
      if (currentStep === 1 && aiEnabled && !specialQuote()) { if (preview.status === 'ready') goToStep(2); else generate(); }
      else goToStep(currentStep + 1);
    });
    listen(prevButton, 'click', () => goToStep(currentStep - 1));
    qa('[data-ft-step-target]').forEach((button) => listen(button, 'click', () => goToStep(Number(button.dataset.ftStepTarget))));
    listen(q('[data-ft-generate-preview]'), 'click', () => generate());
    listen(q('[data-ft-check-preview]'), 'click', async () => {
      if (!preview.pendingId || !session?.token || busy) return;
      busy = true; const version = generationVersion; startGenerationProgress(true); updatePreview();
      try {
        const result = await jsonResponse(await api(`/api/previews/${encodeURIComponent(preview.pendingId)}`, { token: session.token, timeout: 20000 }));
        if (result.status === 'ready') await installReady(result, session.token, true, version, preview.pendingChangeRequest || '');
        else if (result.status === 'failed') { preview.pendingId = ''; throw new Error(result.error?.message || 'This preview could not be completed. You can try different photos or continue with a designer.'); }
        else text('[data-ft-preview-status]', 'Your preview is still being created. Check again in a little while; no new generation has started.');
      } catch (failure) { if (failure.status === 404) preview.pendingId = ''; error('[data-ft-preview-error]', failure.message); }
      finally { stopGenerationProgress(); busy = false; updatePreview(); }
    });
    listen(q('[data-ft-regenerate]'), 'click', () => {
      const change = value('[data-ft-preview-change]');
      if (!change) { error('[data-ft-preview-error]', 'Tell us what you’d like to adjust first.'); q('[data-ft-preview-change]').focus(); return; }
      generate(change);
    });
    listen(q('[data-ft-preview-change]'), 'input', () => text('[data-ft-revision-count]', `${value('[data-ft-preview-change]').length}/300`));
    qa('[data-ft-change-suggestion]').forEach((button) => listen(button, 'click', () => {
      const input = q('[data-ft-preview-change]');
      const current = input.value.trim();
      const suggestion = button.dataset.ftChangeSuggestion;
      if (current.includes(suggestion)) { input.focus(); return; }
      const next = [current, suggestion].filter(Boolean).join(' ');
      if (next.length > 300) { error('[data-ft-preview-error]', 'Please shorten your changes before adding another suggestion.'); input.focus(); return; }
      input.value = next; text('[data-ft-revision-count]', `${next.length}/300`); input.focus();
    }));
    listen(q('[data-ft-preview-versions]'), 'click', (event) => {
      const button = event.target.closest('[data-ft-preview-version]');
      if (!button || busy || submitting || restoring || preview.pendingId) return;
      const selected = previewHistory.find((entry) => entry.id === button.dataset.ftPreviewVersion);
      if (!selected || selected.id === preview.id) return;
      preview = { ...selected, accepted: false, manual: false };
      q('[data-ft-preview-change]').value = '';
      setOrderPreview(); updateInquiry(); updateReview(); updatePreview();
      error('[data-ft-preview-error]', '');
      text('[data-ft-preview-status]', `${button.textContent.trim()} is selected. Choose a size when you’re happy with it, or adjust this version.`);
      const link = privateLink(); if (link) window.history.replaceState(null, '', link);
    });
    listen(q('[data-ft-manual-preview]'), 'click', () => {
      if (busy || restoring || !validateDetails()) return;
      preview.manual = true; preview.accepted = false; setOrderPreview(); showStep(3);
    });
    listen(q('[data-ft-copy-preview]'), 'click', async () => {
      const link = privateLink(); if (!link) return;
      try { await navigator.clipboard.writeText(link); text('[data-ft-preview-status]', 'Private link copied. Anyone with the link can view the preview and reference photos.'); }
      catch (_error) { window.prompt('Copy this private preview link. Share only with people you choose:', link); }
    });
    listen(q('[data-ft-delete-preview]'), 'click', async () => {
      if (!preview.owner || !session?.token || busy) return;
      if (!window.confirm('Delete this preview and its stored reference photos? Your selected files on this page will also be cleared.')) return;
      busy = true; updateActions();
      try {
        const response = await api(`/api/previews/${encodeURIComponent(preview.id)}`, { method: 'DELETE', token: session.token, timeout: 20000 });
        if (!response.ok) await jsonResponse(response);
        busy = false; invalidatePreview('Preview deleted. Add photos to begin a new design.'); photoInputs.forEach(clearPhoto); consent.checked = false; updatePhotoSlots(); showStep(1);
      } catch (failure) { error('[data-ft-preview-error]', failure.message || 'The preview could not be deleted. Please contact us for help.'); }
      finally { busy = false; updateActions(); }
    });
    listen(form, 'change', (event) => {
      if (event.target.matches('[data-ft-photo-input]')) return;
      if (event.target.matches('[data-ft-option]')) {
        const group = event.target.closest('[data-ft-option-group]');
        if (group.dataset.ftOptionName !== 'Size') invalidatePreview('Your subjects changed. Create a fresh preview or continue with a designer.');
        updateOptions();
      } else {
        if (event.target.matches('[data-ft-larger-group], [data-ft-style], [data-ft-kit-subject]')) invalidatePreview('Your design details changed. Create a fresh preview or continue with a designer.');
        updatePrices();
      }
      updatePhotoSlots(); error('[data-ft-submit-error]', '');
    });
    listen(form, 'input', (event) => {
      if (event.target.matches('[data-ft-base-text], [data-ft-notes]')) { invalidatePreview('Your design details changed. Create a fresh preview or continue with a designer.'); updateInquiry(); updateReview(); }
    });
    listen(variantSelect, 'change', () => {
      const variant = variants.find((item) => String(item.id) === variantSelect.value); if (!variant) return;
      const changedSubjects = selectedVariant?.options[0] !== variant.options[0];
      optionGroups.forEach((group) => { group.querySelectorAll('[data-ft-option]').forEach((input) => { input.checked = input.value === variant.options[Number(group.dataset.ftOptionGroup)]; }); });
      selectedVariant = variant; if (changedSubjects) invalidatePreview(); updateOptions();
    });
    photoInputs.forEach((input) => {
      listen(input, 'change', () => handlePhoto(input));
      listen(input.closest('[data-ft-photo-slot]').querySelector('[data-ft-photo-remove]'), 'click', () => { clearPhoto(input); invalidatePreview('Your photos changed. Create a fresh preview or continue with a designer.'); error('[data-ft-file-error]', ''); updatePhotoSlots(); });
    });
    listen(form, 'invalid', (event) => { const panel = event.target.closest('[data-ft-step-panel]'); if (panel) showStep(Number(panel.dataset.ftStepPanel), false); const details = event.target.closest('details'); if (details) details.open = true; }, { capture: true });
    listen(q('[data-ft-inquiry]'), 'click', (event) => { updateInquiry(); if (!validateDetails(false)) event.preventDefault(); });
    listen(form, 'submit', (event) => {
      if (submitting || busy || restoring) { event.preventDefault(); return; }
      if (wizardEnabled && currentStep < 3) { event.preventDefault(); nextButton.click(); return; }
      if (!purchasing()) { event.preventDefault(); updateInquiry(); if (validateDetails(false)) window.location.href = q('[data-ft-inquiry]').href; return; }
      if (!validateDetails(true) || !validateVariant()) { event.preventDefault(); return; }
      if (wizardEnabled && aiEnabled && !preview.accepted && !preview.manual) { event.preventDefault(); showStep(2); error('[data-ft-preview-error]', 'Select your preview or continue with a designer before checkout.'); return; }
      submitDestination = event.submitter?.dataset.ftDestination === 'cart' ? 'cart' : 'checkout';
      const returnTo = q('[data-ft-return-to]'); returnTo.value = submitDestination === 'cart' ? returnTo.dataset.cartUrl : returnTo.dataset.checkoutUrl;
      submitting = true; updateActions();
      // Keep native multipart submission: all original files and the accepted AI file remain enabled.
    });
    listen(window, 'pageshow', () => { submitting = false; updatePrices(); updatePhotoSlots(); });
    root.classList.add('is-enhanced');
    show('[data-ft-enhanced-options], [data-ft-size-options]', true);
    show('[data-ft-ai-privacy]', aiEnabled); if (aiEnabled) q('[data-ft-privacy-link]').href = `${endpoint}/privacy`;
    if (wizardEnabled) { form.noValidate = true; progress.hidden = false; root.classList.add('has-wizard'); showStep(1, false); }
    updateOptions(); updatePhotoSlots(); updatePreview();
    instances.set(root, () => {
      controller.abort(); generationVersion += 1; stopGenerationProgress();
      objectUrls.forEach((url) => URL.revokeObjectURL(url)); releasePreviewImages();
      form.noValidate = initialNoValidate; instances.delete(root);
    });
    if (session) applyLimits(session);
    restoreFromLink();
    if (aiEnabled && !window.location.hash.startsWith('#preview=')) getSession().catch(() => {});
  }
  function initialize(scope) { if (scope.matches?.('[data-ft-product]')) initProduct(scope); scope.querySelectorAll?.('[data-ft-product]').forEach(initProduct); }
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => { const products = event.target.matches?.('[data-ft-product]') ? [event.target] : event.target.querySelectorAll('[data-ft-product]'); products.forEach((root) => instances.get(root)?.()); });
})();
